import { TypographyStyle, TypographyVariantsOptions } from '@mui/material/styles';

import { FontFamily, FontWeight } from './constants';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    body3: TypographyStyle;
    buttonLarge: TypographyStyle;
    buttonMedium: TypographyStyle;
    buttonSmall: TypographyStyle;
    inputText: TypographyStyle;
    inputLabel: TypographyStyle;
    inputHelperText: TypographyStyle;
    menuItem: TypographyStyle;
    avatarInitials: TypographyStyle;
    badgeLabel: TypographyStyle;
    chip: TypographyStyle;
    tooltip: TypographyStyle;
    tableHeader: TypographyStyle;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    body3?: TypographyStyle;
    buttonLarge?: TypographyStyle;
    buttonMedium?: TypographyStyle;
    buttonSmall?: TypographyStyle;
    inputText?: TypographyStyle;
    inputLabel?: TypographyStyle;
    inputHelperText?: TypographyStyle;
    menuItem?: TypographyStyle;
    avatarInitials?: TypographyStyle;
    badgeLabel?: TypographyStyle;
    chip?: TypographyStyle;
    tooltip?: TypographyStyle;
    tableHeader?: TypographyStyle;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true;
    buttonLarge: true;
    buttonMedium: true;
    buttonSmall: true;
    inputText: true;
    inputLabel: true;
    inputHelperText: true;
    menuItem: true;
    avatarInitials: true;
    badgeLabel: true;
    chip: true;
    tooltip: true;
    tableHeader: true;
  }
}

const typographySharedOptions = {
  fontFamily: FontFamily,
} as const satisfies TypographyStyle;

export const typography = {
  fontFamily: FontFamily,

  h1: {
    ...typographySharedOptions,
    fontSize: 96,
  },
  h2: {
    ...typographySharedOptions,
    fontSize: 60,
  },
  h3: {
    ...typographySharedOptions,
    fontSize: 48,
  },
  h4: {
    ...typographySharedOptions,
    fontSize: 34,
  },
  h5: {
    ...typographySharedOptions,
    fontSize: 24,
  },
  h6: {
    ...typographySharedOptions,
    fontSize: 20,
  },

  subtitle1: {
    ...typographySharedOptions,
    fontSize: 16,
    fontWeight: FontWeight.SemiBold,
  },
  subtitle2: {
    ...typographySharedOptions,
    fontSize: 14,
    fontWeight: FontWeight.SemiBold,
  },

  body1: {
    ...typographySharedOptions,
    fontSize: 16,
  },
  body2: {
    ...typographySharedOptions,
    fontSize: 14,
  },
  body3: {
    ...typographySharedOptions,
    fontSize: 12,
    fontWeight: FontWeight.Regular,
    lineHeight: 1.4,
    letterSpacing: 0.15,
  },

  buttonLarge: {
    ...typographySharedOptions,
    fontSize: 16,
    fontWeight: FontWeight.Medium,
    textTransform: 'uppercase',
  },
  buttonMedium: {
    ...typographySharedOptions,
    fontSize: 14,
    fontWeight: FontWeight.Medium,
    textTransform: 'uppercase',
  },
  buttonSmall: {
    ...typographySharedOptions,
    fontSize: 13,
    fontWeight: FontWeight.Medium,
    textTransform: 'uppercase',
  },

  inputText: {
    ...typographySharedOptions,
    fontSize: 16,
    fontWeight: FontWeight.Regular,
    letterSpacing: 0.15,
  },
  inputLabel: {
    ...typographySharedOptions,
    fontSize: 16,
    fontWeight: FontWeight.Regular,
    letterSpacing: 0.4,
  },
  inputHelperText: {
    ...typographySharedOptions,
    fontSize: 12,
    fontWeight: FontWeight.Regular,
    letterSpacing: 0.15,
  },
  menuItem: {
    ...typographySharedOptions,
    fontSize: 16,
    fontWeight: FontWeight.Regular,
    letterSpacing: 0.15,
    lineHeight: 1.5,
  },

  overline: {
    ...typographySharedOptions,
    fontSize: 12,
    letterSpacing: 1,
  },
  caption: {
    ...typographySharedOptions,
    fontSize: 12,
  },

  avatarInitials: {
    ...typographySharedOptions,
    fontSize: 20,
    fontWeight: FontWeight.Regular,
    letterSpacing: 0.15,
  },
  badgeLabel: {
    ...typographySharedOptions,
    fontSize: 12,
    fontWeight: FontWeight.Medium,
    letterSpacing: 0.15,
  },
  chip: {
    ...typographySharedOptions,
    fontSize: 13,
    fontWeight: FontWeight.Regular,
    letterSpacing: 0.15,
  },
  tooltip: {
    ...typographySharedOptions,
    fontSize: 10,
    fontWeight: FontWeight.Medium,
    letterSpacing: 0.15,
  },
  tableHeader: {
    ...typographySharedOptions,
    fontSize: 14,
    fontWeight: FontWeight.Medium,
    letterSpacing: 0.15,
  },
} as const satisfies TypographyVariantsOptions;
